<template>
  <div>
    <HrPageLayout
      title="Performance Appraisal - Self Assessment"
      subtitle="Annual performance review"
      icon="📋"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Appraisals', route: '/my-appraisals' },
        { label: 'Self Assessment' }
      ]"
    >
      <div class="self-assessment-content">
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body">
            <div class="self-assessment-header">
              <div>
                <h4 class="hr-mb-1">
                  <i class="bx bx-file-blank hr-me-2 hr-text-primary"></i>
                  Performance Appraisal - Self Assessment
                </h4>
                <p class="hr-text-muted hr-mb-0">{{ appraisalYear }} Annual Performance Review</p>
              </div>
              <div class="hr-text-end">
                <span class="hr-badge hr-badge-primary hr-px-3 hr-py-2">Status: {{ status }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body">
            <div class="hr-flex hr-justify-between hr-align-center hr-mb-3">
              <span class="hr-text-muted hr-small">Progress: {{ progressPercentage }}%</span>
              <span class="hr-text-muted hr-small">Section {{ currentSection + 1 }} of 6</span>
            </div>
            <div class="hr-progress-wrap" style="height: 8px;">
              <div class="hr-progress-fill hr-bg-primary" :style="{ width: progressPercentage + '%' }"></div>
            </div>
            <div class="hr-flex hr-justify-between hr-mt-3 hr-flex-wrap">
              <span v-for="(section, index) in sections" :key="index"
                    :class="['hr-badge hr-section-tab', index <= currentSection ? 'hr-badge-primary' : 'hr-badge-secondary']"
                    style="cursor: pointer;"
                    @click="goToSection(index)">
                {{ section.short }}
              </span>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-body">

                  <!-- Appraisal Details Header -->
                  <div v-if="currentSection === 0" class="hr-border-bottom hr-pb-4 hr-mb-4">
                    <h5 class="hr-text-primary hr-mb-3">
                      <i class="bx bx-info-circle hr-me-2"></i>
                      Appraisal Details
                    </h5>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="hr-form-label">Associate Name</label>
                        <input type="text" class="hr-input" v-model="employeeInfo.name" disabled>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="hr-form-label">Position Title</label>
                        <input type="text" class="hr-input" v-model="employeeInfo.position" disabled>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="hr-form-label">Department</label>
                        <input type="text" class="hr-input" v-model="employeeInfo.department" disabled>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="hr-form-label">Period Start Date</label>
                        <input type="date" class="hr-input" v-model="appraisal.periodStart" required>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="hr-form-label">Period End Date</label>
                        <input type="date" class="hr-input" v-model="appraisal.periodEnd" required>
                      </div>
                    </div>
                  </div>

                  <!-- Section A: KPAs -->
                  <div v-if="currentSection === 0">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-target hr-me-2"></i>
                      Section A: Performance - Key Performance Areas
                    </h5>

                    <!-- KPAs Table -->
                    <div class="hr-table-wrapper hr-mb-4">
                      <table class="hr-table hr-table-bordered">
                        <thead>
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="20%">Key Performance Area (KPA)</th>
                          <th width="25%">Results Achieved</th>
                          <th width="10%">Self Rating (1-5)</th>
                          <th width="10%">Weightage</th>
                          <th width="5%">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(kpa, index) in kpas" :key="index">
                          <td class="hr-text-center">{{ index + 1 }}</td>
                          <td>
                            <textarea v-if="!kpasPreDefined" v-model="kpa.kpa" class="hr-input" rows="3"
                                      placeholder="Enter KPA" required></textarea>
                            <span v-else class="d-block p-2 bg-light rounded">{{ kpa.kpa || '–' }}</span>
                          </td>
                          <td>
                        <textarea v-model="kpa.resultsAchieved" class="hr-input" rows="3"
                                  placeholder="Describe your achievements"></textarea>
                          </td>
                          <td>
                            <select v-model="kpa.selfRating" class="hr-select" required>
                              <option value="">Select</option>
                              <option value="5">5 - Outstanding</option>
                              <option value="4">4 - Exceeds Expectations</option>
                              <option value="3">3 - Met Expectations</option>
                              <option value="2">2 - Below Expectations</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </td>
                          <td>
                            <input v-if="!kpasPreDefined" type="number" v-model="kpa.weightage" class="hr-input"
                                   min="0" max="100" step="0.01">
                            <span v-else class="d-block p-2 text-center">{{ kpa.weightage }}%</span>
                          </td>
                          <td class="hr-text-center">
                            <button v-if="!kpasPreDefined && kpas.length > 1" class="hr-btn hr-btn-sm hr-btn-danger" @click="removeKPA(index)">
                              <i class="bx bx-trash"></i>
                            </button>
                            <span v-else class="text-muted">–</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-if="kpas.length === 0" class="hr-alert hr-alert-warning hr-mb-4">
                      <i class="bx bx-info-circle hr-me-2"></i>
                      <strong>No KPAs assigned.</strong> KPAs are set by HR or your supervisor. Please contact them to assign KPAs for your department before you can complete this section.
                    </div>

                    <button v-if="!kpasPreDefined" class="hr-btn hr-btn-outline hr-btn-primary hr-mb-4" @click="addKPA">
                      <i class="bx bx-plus hr-me-1"></i> Add Another KPA
                    </button>

                    <!-- Section A Overall Rating Display -->
                    <div class="hr-alert hr-alert-success" v-if="calculatedSectionA">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-0">
                            <i class="bx bx-calculator hr-me-2"></i>
                            Section A: Your Overall Self Rating
                          </h6>
                          <small class="text-muted">Weighted average based on KPA ratings</small>
                        </div>
                        <div class="text-end">
                          <h3 class="mb-0 text-success">{{ calculatedSectionA }}</h3>
                          <small class="text-muted">out of 5.00</small>
                        </div>
                      </div>
                    </div>

                    <!-- Key Organizational Parameters -->
                    <h6 class="hr-text-primary hr-mb-3 hr-mt-4">Key Organizational Parameters</h6>
                    <div class="hr-table-wrapper">
                      <table class="hr-table hr-table-bordered">
                        <thead>
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="35%">Question</th>
                          <th width="10%">Answer (1-5)</th>
                          <th width="50%">Associate Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(param, index) in organizationalParams" :key="index">
                          <td class="hr-text-center">{{ index + 1 }}</td>
                          <td>{{ param.question }}</td>
                          <td>
                            <select v-model="param.answer" class="hr-select" required>
                              <option value="">Select</option>
                              <option value="5">5 - Outstanding</option>
                              <option value="4">4 - Exceeds Expectations</option>
                              <option value="3">3 - Met Expectations</option>
                              <option value="2">2 - Below Expectations</option>
                              <option value="1">1 - Poor</option>
                            </select>
                          </td>
                          <td>
                        <textarea v-model="param.associateComments" class="hr-input" rows="2"
                                  placeholder="Enter your comments"></textarea>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section 2(B): Staff Training and Development Needs -->
                  <div v-if="currentSection === 1">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-book-reader hr-me-2"></i>
                      Section 2(B): Staff Training and Development Needs
                    </h5>

                    <div class="hr-alert hr-alert-info">
                      <i class="bx bx-info-circle hr-me-2"></i>
                      List your training and development needs in order of priority.
                    </div>

                    <div class="hr-table-wrapper">
                      <table class="hr-table hr-table-bordered">
                        <thead>
                        <tr>
                          <th width="10%">Priority</th>
                          <th width="90%">Training and development need</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(need, index) in trainingNeeds" :key="index">
                          <td class="hr-text-center">{{ index + 1 }}</td>
                          <td>
                            <input v-model="trainingNeeds[index]" class="hr-input" placeholder="Enter training need">
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section 2(C) & 3(A): Commitment and Mid-Year Review -->
                  <div v-if="currentSection === 2">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-file hr-me-2"></i>
                      Section 2(C) & 3(A): Commitment and Mid-Year Review
                    </h5>
                    <div class="card bg-light mb-4">
                      <div class="card-body">
                        <div class="form-check mb-2">
                          <input class="form-check-input" type="checkbox" v-model="commitmentToTargets" id="commitTargets">
                          <label class="form-check-label" for="commitTargets">
                            I commit to the agreed individual performance targets in Section 2(A).
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label fw-bold">Mid-Year Review Notes (Section 3A)</label>
                      <textarea v-model="midYearReviewNotes" class="hr-input" rows="6" placeholder="Document target changes/additions and achievement remarks"></textarea>
                    </div>
                  </div>

                  <!-- Section 3(B): Annual Report (Appraisee side) -->
                  <div v-if="currentSection === 3">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-clipboard hr-me-2"></i>
                      Section 3(B): Annual Appraisal Report (Appraisee Side)
                    </h5>

                    <div class="hr-alert hr-alert-info">
                      <i class="bx bx-info-circle hr-me-2"></i>
                      Fill only your achieved results and self score. Appraiser columns are completed by the supervisor.
                    </div>

                    <div class="hr-table-wrapper">
                      <table class="hr-table hr-table-bordered">
                        <thead>
                        <tr>
                          <th width="5%">S.No</th>
                          <th width="30%">Agreed Performance Target</th>
                          <th width="45%">Achieved Results/Score (By Appraisee)</th>
                          <th width="20%">Self Score (1-5)</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(kpa, index) in kpas" :key="index">
                          <td class="hr-text-center">{{ index + 1 }}</td>
                          <td>
                            <span class="d-block p-2 bg-light rounded">{{ kpa.kpa || '-' }}</span>
                          </td>
                          <td>
                            <textarea v-model="kpa.resultsAchieved" class="hr-input" rows="2" placeholder="Enter achieved results"></textarea>
                          </td>
                          <td>
                            <select v-model="kpa.selfRating" class="hr-select">
                              <option value="">Select</option>
                              <option value="5">5</option>
                              <option value="4">4</option>
                              <option value="3">3</option>
                              <option value="2">2</option>
                              <option value="1">1</option>
                            </select>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section 4: Appraisee comments and additional assignments -->
                  <div v-if="currentSection === 4">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-comment-detail hr-me-2"></i>
                      Section 4: Appraisee's Comments and Additional Assignments
                    </h5>

                    <div class="mb-4">
                      <label class="form-label fw-bold">a) Appraisee comments on performance (including mitigating factors)</label>
                      <textarea v-model="section4Comments" class="hr-input" rows="6" placeholder="Enter your comments"></textarea>
                    </div>

                    <label class="form-label fw-bold">b) Additional Assignments</label>
                    <div class="hr-table-wrapper">
                      <table class="hr-table hr-table-bordered">
                        <thead>
                        <tr>
                          <th width="10%">No.</th>
                          <th width="90%">Assignment</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(assignment, index) in additionalAssignments" :key="index">
                          <td class="hr-text-center">{{ index + 1 }}</td>
                          <td>
                            <input v-model="additionalAssignments[index]" class="hr-input" placeholder="Enter additional assignment">
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Section F: Review & Submit -->
                  <div v-if="currentSection === 5">
                    <h5 class="hr-text-primary hr-mb-4">
                      <i class="bx bx-check-circle hr-me-2"></i>
                      Review & Submit
                    </h5>

                    <div class="alert alert-warning">
                      <i class="bx bx-alert hr-me-2"></i>
                      Please review all sections before submitting. Once submitted, your appraisal will be sent to your appraiser for review.
                    </div>

                    <!-- Summary Cards -->
                    <div class="row mb-4">
                      <div class="col-md-3">
                        <div class="card bg-success text-white">
                          <div class="hr-chart-body hr-text-center">
                            <h3 class="text-white">{{ calculatedSectionA || '-' }}</h3>
                            <p class="mb-0">Section A Rating</p>
                            <small>KPA Performance</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-info text-white">
                          <div class="hr-chart-body hr-text-center">
                            <h3 class="text-white">{{ trainingNeeds.filter(t => (t || '').trim()).length }}</h3>
                            <p class="mb-0">Section 2(B)</p>
                            <small>Training Needs</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-primary text-white">
                          <div class="hr-chart-body hr-text-center">
                            <h3 class="text-white">{{ additionalAssignments.filter(a => (a || '').trim()).length }}</h3>
                            <p class="mb-0">Section 4</p>
                            <small>Assignments Listed</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="card bg-warning text-white">
                          <div class="hr-chart-body hr-text-center">
                            <h3 class="text-white">{{ completionPercentage }}%</h3>
                            <p class="mb-0">Completion</p>
                            <small>Overall Progress</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Sections Review -->
                    <div class="card mb-3" v-for="(section, index) in sections" :key="index">
                      <div class="card-header d-flex justify-content-between align-items-center">
                  <span>
                    <i :class="['mdi', section.icon, 'me-2']"></i>
                    {{ section.name }}
                  </span>
                        <span :class="['badge', getSectionCompletionBadge(index)]">
                    {{ getSectionStatus(index) }}
                  </span>
                      </div>
                    </div>

                    <!-- Final Comments -->
                    <div class="mb-4">
                      <label class="form-label fw-bold">Any additional comments or concerns:</label>
                      <textarea v-model="section4Comments" class="hr-input" rows="4"
                                placeholder="Optional: Add any final comments..."></textarea>
                    </div>

                    <!-- Digital Signature -->
                    <div class="card bg-light mb-4">
                      <div class="card-body">
                        <h6 class="mb-3">Digital Signature</h6>
                        <div class="form-check mb-3">
                          <input class="form-check-input" type="checkbox" v-model="signatureAgreement" id="signatureCheck">
                          <label class="form-check-label" for="signatureCheck">
                            I certify that the information provided in this self-assessment is accurate and complete to the best of my knowledge.
                          </label>
                        </div>
                        <div v-if="signatureAgreement" class="border p-3 bg-white">
                          <p class="mb-2"><strong>Employee Name:</strong> {{ employeeInfo.name }}</p>
                          <p class="mb-2"><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</p>
                          <p class="mb-0 text-muted small">
                            <i class="bx bx-shield-check hr-me-1"></i>
                            Digital signature captured
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Navigation Buttons -->
                  <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                    <div>
                      <button class="hr-btn hr-btn-outline hr-btn-secondary" @click="previousSection" v-if="currentSection > 0">
                        <i class="bx bx-arrow-left hr-me-1"></i> Previous
                      </button>
                    </div>
                    <div>
                      <button class="hr-btn hr-btn-outline hr-btn-primary hr-me-2" @click="saveDraft" :disabled="saving">
                        <i class="bx bx-save hr-me-1"></i>
                        {{ saving ? 'Saving...' : 'Save as Draft' }}
                      </button>
                      <button class="hr-btn hr-btn-primary" @click="nextSection" v-if="currentSection < sections.length - 1">
                        Next <i class="bx bx-arrow-right hr-ms-1"></i>
                      </button>
                      <button class="hr-btn hr-btn-success" @click="submitAppraisal" v-if="currentSection === sections.length - 1"
                              :disabled="!signatureAgreement || submitting">
                        <i class="bx bx-send hr-me-1"></i>
                        {{ submitting ? 'Submitting...' : 'Submit for Review' }}
                      </button>
                    </div>
                  </div>

                </div>
              </div>

          <!-- Rating Guide Modal -->
          <div class="modal fade" id="ratingGuideModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Rating Scale Guide</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModalFromEvent($event)"></button>
                </div>
                <div class="modal-body">
                  <div class="hr-table-wrapper">
                    <table class="hr-table hr-table-bordered">
                      <thead>
                      <tr>
                        <th>Rating</th>
                        <th>Description</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td><strong>5 - Outstanding</strong></td>
                        <td>
                          • Met all aspects of job requirements and exceeded expectations in all aspects<br>
                          • Made major and notable contributions<br>
                          • Demonstrated creativity, initiative, flexibility
                        </td>
                      </tr>
                      <tr>
                        <td><strong>4 - Exceeds Expectations</strong></td>
                        <td>
                          • Met all aspects of job description and exceeded in most respects<br>
                          • Made contributions to the company
                        </td>
                      </tr>
                      <tr>
                        <td><strong>3 - Met Expectations</strong></td>
                        <td>
                          • Met basic job requirements<br>
                          • Task completed within time frame with efficiency and initiative<br>
                          • Fully accomplished expectations
                        </td>
                      </tr>
                      <tr>
                        <td><strong>2 - Below Expectations</strong></td>
                        <td>
                          • Did not consistently meet job requirements<br>
                          • Needs improvement in key areas
                        </td>
                      </tr>
                      <tr>
                        <td><strong>1 - Poor</strong></td>
                        <td>
                          • Did not meet minimum job requirements<br>
                          • Significant performance issues
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeSelfAssessment",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      saving: false,
      submitting: false,
      currentSection: 0,
      appraisalId: null,
      status: 'Draft',
      appraisalYear: new Date().getFullYear(),
      signatureAgreement: false,
      /** When true, KPAs come from template or existing appraisal – employee cannot add/remove/edit KPA titles */
      kpasPreDefined: false,

      sections: [
        { name: 'Section 1 & 2(A): Employment Details and Agreed Targets', short: 'S1+2A', icon: 'mdi-target' },
        { name: 'Section 2(B): Staff Training and Development Needs', short: 'S2B', icon: 'mdi-school' },
        { name: 'Section 2(C) & 3(A): Commitment and Mid-Year Review', short: 'S2C+3A', icon: 'mdi-file-sign' },
        { name: 'Section 3(B): Annual Appraisal Report (Appraisee Side)', short: 'S3B', icon: 'mdi-clipboard-text' },
        { name: 'Section 4: Appraisee Comments and Additional Assignments', short: 'S4', icon: 'mdi-comment-text' },
        { name: 'Review & Submit', short: 'Review', icon: 'mdi-check-circle' }
      ],

      employeeInfo: {
        id: null,
        name: '',
        position: '',
        department: '',
        location: '',
        dateOfJoining: ''
      },

      appraisal: {
        periodStart: '',
        periodEnd: '',
        associateComments: ''
      },

      kpas: [],
      organizationalParams: [],

      attributes: [],

      jobContent: [],

      growthOpportunities: [],

      workCulture: [],

      colleagues: [],

      trainings: [],

      additionalFeedback: {
        thingsIShouldDo: '',
        managerShouldDo: '',
        organizationShouldDo: ''
      },

      goals: [],
      trainingNeeds: ['', '', '', '', ''],
      commitmentToTargets: false,
      midYearReviewNotes: '',
      section4Comments: '',
      additionalAssignments: ['', '', '', '', '']
    };
  },

  computed: {
    progressPercentage() {
      return Math.round(((this.currentSection + 1) / this.sections.length) * 100);
    },

    completionPercentage() {
      let completed = 0;
      let total = 6;

      // Check each section
      if (this.kpas.length > 0 && this.kpas.every(k => k.kpa && k.selfRating)) completed++;
      if (this.trainingNeeds.some(t => (t || '').trim())) completed++;
      if (this.commitmentToTargets && (this.midYearReviewNotes || '').trim()) completed++;
      if (this.kpas.length > 0 && this.kpas.every(k => (k.resultsAchieved || '').trim() && k.selfRating)) completed++;
      if ((this.section4Comments || '').trim()) completed++;

      return Math.round((completed / total) * 100);
    },

    // Auto-calculate Section A Overall Self Rating (Weighted Average)
    calculatedSectionA() {
      if (!this.kpas || this.kpas.length === 0) return null;

      let totalWeightedScore = 0;
      let totalWeight = 0;

      for (const kpa of this.kpas) {
        if (kpa.selfRating && kpa.weightage) {
          const rating = parseFloat(kpa.selfRating);
          const weight = parseFloat(kpa.weightage);
          totalWeightedScore += (rating * weight);
          totalWeight += weight;
        }
      }

      if (totalWeight === 0) return null;
      return (totalWeightedScore / totalWeight).toFixed(2);
    },

    // Auto-calculate Section B Overall Self Rating (Simple Average)
    calculatedSectionB() {
      if (!this.attributes || this.attributes.length === 0) return null;

      let totalRating = 0;
      let count = 0;

      for (const attr of this.attributes) {
        if (attr.selfRating) {
          totalRating += parseFloat(attr.selfRating);
          count++;
        }
      }

      if (count === 0) return null;
      return (totalRating / count).toFixed(2);
    }
  },

  mounted() {
    const existingId = this.$route.query.id;
    if (existingId) {
      this.appraisalId = existingId;
      this.loadExistingAppraisalForEdit();
    } else {
      this.loadEmployeeInfo();
      this.loadStandardAttributes();
      this.loadKPATemplates();
      this.loadFeedbackQuestions();
      this.loadTrainingPrograms();
      this.initializeGoals();
    }

    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    this.appraisal.periodEnd = this.appraisal.periodEnd || today.toISOString().split('T')[0];
    this.appraisal.periodStart = this.appraisal.periodStart || sixMonthsAgo.toISOString().split('T')[0];
  },

  methods: {
    async loadExistingAppraisalForEdit() {
      try {
        const data = new FormData();
        data.append("function", "getAppraisalDetails");
        data.append("appraisalId", this.appraisalId);
        const res = await execute(data, authUrl);
        if (!res || !res.data || !res.data.success || !res.data.data) return;
        const d = res.data.data;
        this.status = d.appraisal?.status || 'Draft';
        if (d.appraisal) {
          this.appraisal.periodStart = (d.appraisal.periodStart || '').split(' ')[0] || this.appraisal.periodStart;
          this.appraisal.periodEnd = (d.appraisal.periodEnd || '').split(' ')[0] || this.appraisal.periodEnd;
          this.appraisal.associateComments = d.appraisal.associateComments || '';
          this.section4Comments = d.appraisal.associateComments || '';
        }
        this.employeeInfo.name = d.appraisal?.employeeName || this.employeeInfo.name;
        this.employeeInfo.position = d.appraisal?.positionTitle || this.employeeInfo.position;
        this.employeeInfo.department = d.appraisal?.department || this.employeeInfo.department;
        if (d.kpas && d.kpas.length > 0) {
          this.kpas = d.kpas.map((k, i) => ({
            id: k.id,
            sNo: k.sNo ?? i + 1,
            kpa: k.kpa,
            resultsAchieved: k.resultsAchieved || '',
            selfRating: k.selfRating ? String(k.selfRating) : '',
            weightage: k.weightage ?? 10
          }));
          this.kpasPreDefined = true;
        }
        if (d.attributes && d.attributes.length > 0) {
          this.attributes = d.attributes.map(a => ({ ...a, selfRating: a.selfRating ? String(a.selfRating) : '' }));
        }
        if (d.organizationalParams && d.organizationalParams.length > 0) this.organizationalParams = d.organizationalParams;
        if (d.jobContent && d.jobContent.length > 0) this.jobContent = d.jobContent;
        if (d.growthOpportunities && d.growthOpportunities.length > 0) this.growthOpportunities = d.growthOpportunities;
        if (d.workCulture && d.workCulture.length > 0) this.workCulture = d.workCulture;
        if (d.colleagues && d.colleagues.length > 0) this.colleagues = d.colleagues;
        if (d.trainings && d.trainings.length > 0) {
          this.trainings = d.trainings;
          const needs = d.trainings
            .map(t => t.trainingProgram || '')
            .filter(v => (v || '').trim() !== '')
            .slice(0, 5);
          this.trainingNeeds = [...needs, ...Array(Math.max(0, 5 - needs.length)).fill('')];
        }
        if (d.additionalFeedback) this.additionalFeedback = d.additionalFeedback;
        if (d.additionalFeedback && (d.additionalFeedback.managerShouldDo || '').toLowerCase().includes('committed')) {
          this.commitmentToTargets = true;
        }
        if (d.additionalFeedback && d.additionalFeedback.thingsIShouldDo) {
          this.midYearReviewNotes = d.additionalFeedback.thingsIShouldDo;
        }
        if (d.goals && d.goals.length > 0) {
          this.goals = d.goals;
          const assignments = d.goals
            .map(g => g.actionPlan || '')
            .filter(v => (v || '').trim() !== '')
            .slice(0, 5);
          this.additionalAssignments = [...assignments, ...Array(Math.max(0, 5 - assignments.length)).fill('')];
        }
        if (this.attributes.length === 0) this.loadStandardAttributes();
        if (this.organizationalParams.length === 0) this.loadFeedbackQuestions();
        if (this.trainings.length === 0) this.loadTrainingPrograms();
        if (this.goals.length === 0) this.initializeGoals();
      } catch (e) {
        console.error("Failed to load existing appraisal:", e);
        this.loadEmployeeInfo();
        this.loadStandardAttributes();
        this.loadKPATemplates();
        this.loadFeedbackQuestions();
        this.loadTrainingPrograms();
        this.initializeGoals();
      }
    },

    async loadEmployeeInfo() {
      try {
        const data = new FormData();
        data.append("function", "getEmployeeInfo");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          const emp = res.data.data;
          this.employeeInfo = {
            id: emp.id,
            name: emp.fullName || `${emp.firstName} ${emp.lastName}`,
            position: emp.designation || emp.position,
            department: emp.departmentName || emp.department,
            location: emp.location || 'Nairobi, Kenya',
            dateOfJoining: emp.hireDate || emp.dateOfJoining
          };
        }
      } catch (error) {
        console.error("Failed to load employee info:", error);
      }
    },

    async loadStandardAttributes() {
      try {
        const data = new FormData();
        data.append("function", "getStandardAttributes");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.attributes = res.data.data.map(attr => ({
            sNo: attr.sNo,
            attribute: attr.attribute,
            description: attr.description,
            selfRating: ''
          }));
        }
      } catch (error) {
        console.error("Failed to load attributes:", error);
        // Use default attributes if API fails
        this.loadDefaultAttributes();
      }
    },

    loadDefaultAttributes() {
      this.attributes = [
        { sNo: 1, attribute: 'Decision & Interface skills', selfRating: '' },
        { sNo: 2, attribute: 'Customer orientation', selfRating: '' },
        { sNo: 3, attribute: 'Domain understanding', selfRating: '' },
        { sNo: 4, attribute: 'Dependability & result orientation', selfRating: '' },
        { sNo: 5, attribute: 'Process orientation', selfRating: '' },
        { sNo: 6, attribute: 'Result orientation', selfRating: '' },
        { sNo: 7, attribute: 'Team work', selfRating: '' },
        { sNo: 8, attribute: 'Conflict Handling', selfRating: '' },
        { sNo: 9, attribute: 'Adaptability', selfRating: '' },
        { sNo: 10, attribute: 'Influencing & Persuading', selfRating: '' }
      ];
    },

    async loadFeedbackQuestions() {
      try {
        const data = new FormData();
        data.append("function", "getFeedbackQuestions");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const questions = res.data.data;
          this.organizationalParams = questions.organizationalParams || [];
          this.jobContent = questions.jobContent || [];
          this.growthOpportunities = questions.growthOpportunities || [];
          this.workCulture = questions.workCulture || [];
          this.colleagues = questions.colleagues || [];
        }
      } catch (error) {
        console.error("Failed to load feedback questions:", error);
        // Load defaults if API fails
        this.loadDefaultFeedbackQuestions();
      }
    },

    loadDefaultFeedbackQuestions() {
      this.organizationalParams = [
        { sNo: 1, question: 'Quality and productivity improvement?', answer: '', associateComments: '' },
        { sNo: 2, question: 'Executing higher responsibilities', answer: '', associateComments: '' },
        { sNo: 3, question: 'Worked for more than one customer at the same time', answer: '', associateComments: '' }
      ];

      this.jobContent = [
        { sNo: 1, attribute: 'Clarity on roles and responsibilities at work', response: '', remarks: '' },
        { sNo: 2, attribute: 'Usage of Expertise, knowledge and potential', response: '', remarks: '' },
        { sNo: 3, attribute: 'Job freedom', response: '', remarks: '' },
        { sNo: 4, attribute: 'Opportunities to Grow', response: '', remarks: '' }
      ];

      this.growthOpportunities = [
        { sNo: 1, attribute: 'Handle higher responsibilities and explore new avenues within the job premises', response: '', remarks: '' },
        { sNo: 2, attribute: 'Growth Opportunities available to the associate in the organization', response: '', remarks: '' },
        { sNo: 3, attribute: 'Opportunities to learn and enhance associates\' skills for professional growth', response: '', remarks: '' }
      ];

      this.workCulture = [
        { sNo: 1, attribute: 'Express views and opinions on issues affecting associate and organization', response: '', remarks: '' },
        { sNo: 2, attribute: 'The policies and processes are transparent', response: '', remarks: '' },
        { sNo: 3, attribute: 'Company work environment', response: '', remarks: '' },
        { sNo: 4, attribute: 'Friends at work place', response: '', remarks: '' },
        { sNo: 5, attribute: 'Work Interest', response: '', remarks: '' }
      ];

      this.colleagues = [
        { sNo: 1, attribute: 'Helpful and cooperative', response: '', remarks: '' },
        { sNo: 2, attribute: 'Potentiality and coherence of the team', response: '', remarks: '' },
        { sNo: 3, attribute: 'Discuss and share all relevant information with the associate', response: '', remarks: '' },
        { sNo: 4, attribute: 'Guidance', response: '', remarks: '' }
      ];
    },

    async loadTrainingPrograms() {
      try {
        const data = new FormData();
        data.append("function", "getTrainingPrograms");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.trainings = res.data.data.map(training => ({
            sNo: training.sNo,
            trainingProgram: training.trainingProgram,
            priority: ''
          }));
        }
      } catch (error) {
        console.error("Failed to load training programs:", error);
        // Load defaults if API fails
        this.loadDefaultTrainingPrograms();
      }
    },

    loadDefaultTrainingPrograms() {
      this.trainings = [
        { sNo: 1, trainingProgram: 'Change Management', priority: '' },
        { sNo: 2, trainingProgram: 'Customer Communications', priority: '' },
        { sNo: 3, trainingProgram: 'Effective Communication and Interpersonal Skills', priority: '' },
        { sNo: 4, trainingProgram: 'Employee Engagement', priority: '' },
        { sNo: 5, trainingProgram: 'Leadership', priority: '' },
        { sNo: 6, trainingProgram: 'Organizational Development', priority: '' },
        { sNo: 7, trainingProgram: 'Presentation Effectiveness', priority: '' }
      ];
    },

    async loadKPATemplates() {
      try {
        const data = new FormData();
        data.append("function", "getKPATemplates");
        data.append("department", this.employeeInfo.department);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          this.kpas = res.data.data.map(kpa => ({
            sNo: kpa.sNo,
            kpa: kpa.kpa,
            resultsAchieved: '',
            selfRating: '',
            weightage: kpa.defaultWeightage || 10
          }));
          this.kpasPreDefined = true;
        } else {
          // No templates – employee cannot create KPAs; they must be assigned by HR/supervisor
          this.kpas = [];
          this.kpasPreDefined = true;
        }
      } catch (error) {
        console.error("Failed to load KPA templates:", error);
        this.kpas = [];
        this.kpasPreDefined = true;
      }
    },

    initializeGoals() {
      // Add 3 empty goals by default
      for (let i = 0; i < 3; i++) {
        this.addGoal();
      }
    },

    addKPA() {
      this.kpas.push({
        sNo: this.kpas.length + 1,
        kpa: '',
        resultsAchieved: '',
        selfRating: '',
        weightage: 10
      });
    },

    removeKPA(index) {
      if (this.kpas.length > 1) {
        this.kpas.splice(index, 1);
        // Renumber
        this.kpas.forEach((kpa, i) => {
          kpa.sNo = i + 1;
        });
      }
    },

    addGoal() {
      this.goals.push({
        sNo: this.goals.length + 1,
        actionPlan: '',
        targetDate: ''
      });
    },

    removeGoal(index) {
      if (this.goals.length > 1) {
        this.goals.splice(index, 1);
        // Renumber
        this.goals.forEach((goal, i) => {
          goal.sNo = i + 1;
        });
      }
    },

    goToSection(index) {
      if (index >= 0 && index < this.sections.length) {
        this.currentSection = index;
        window.scrollTo(0, 0);
      }
    },

    nextSection() {
      if (this.validateCurrentSection()) {
        this.currentSection++;
        window.scrollTo(0, 0);
      }
    },

    previousSection() {
      if (this.currentSection > 0) {
        this.currentSection--;
        window.scrollTo(0, 0);
      }
    },

    validateCurrentSection() {
      switch (this.currentSection) {
        case 0: // Section A
          if (this.kpas.length === 0) {
            this.$toast?.error("No KPAs have been assigned for your department. Please contact HR or your supervisor to set up KPAs before completing your self-assessment.") || alert("No KPAs have been assigned for your department. Please contact HR or your supervisor to set up KPAs before completing your self-assessment.");
            return false;
          }
          for (const kpa of this.kpas) {
            if (!kpa.selfRating) {
              this.$toast?.error("Please provide a self rating for each KPA") || alert("Please provide a self rating for each KPA");
              return false;
            }
            if (this.kpasPreDefined && !(kpa.resultsAchieved || '').trim()) {
              this.$toast?.error("Please describe results achieved for each KPA") || alert("Please describe results achieved for each KPA");
              return false;
            }
            if (!this.kpasPreDefined && (!(kpa.kpa || '').trim() || !kpa.selfRating)) {
              this.$toast?.error("Please complete all KPA fields") || alert("Please complete all KPA fields");
              return false;
            }
          }
          return true;

        case 1: // Section 2(B)
          if (!this.trainingNeeds.some(t => (t || '').trim())) {
            this.$toast?.error("Please provide at least one training need") || alert("Please provide at least one training need");
            return false;
          }
          return true;

        case 2: // Section 2(C) & 3(A)
          if (!this.commitmentToTargets) {
            this.$toast?.error("Please confirm commitment to agreed targets") || alert("Please confirm commitment to agreed targets");
            return false;
          }
          if (!(this.midYearReviewNotes || '').trim()) {
            this.$toast?.error("Please provide mid-year review notes") || alert("Please provide mid-year review notes");
            return false;
          }
          return true;

        case 3: // Section 3(B)
          for (const kpa of this.kpas) {
            if (!(kpa.resultsAchieved || '').trim() || !kpa.selfRating) {
              this.$toast?.error("Please fill achieved results and self score for each target") || alert("Please fill achieved results and self score for each target");
              return false;
            }
          }
          return true;

        case 4: // Section 4
          if (!(this.section4Comments || '').trim()) {
            this.$toast?.error("Please provide your comments on performance") || alert("Please provide your comments on performance");
            return false;
          }
          return true;

        default:
          return true;
      }
    },

    async saveDraft() {
      this.saving = true;

      try {
        const payload = this.preparePayload();
        payload.status = 'Draft';

        const data = new FormData();
        data.append("function", "saveAppraisalDraft");
        data.append("appraisalData", JSON.stringify(payload));
        if (this.appraisalId) {
          data.append("appraisalId", this.appraisalId);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisalId = res.data.data.appraisalId;
          this.$toast?.success("Draft saved successfully") || alert("Draft saved successfully");
        } else {
          throw new Error(res?.data?.message || "Failed to save draft");
        }
      } catch (error) {
        console.error("Error saving draft:", error);
        this.$toast?.error("Failed to save draft") || alert("Failed to save draft");
      } finally {
        this.saving = false;
      }
    },

    async submitAppraisal() {
      if (!this.signatureAgreement) {
        this.$toast?.error("Please agree to the digital signature") || alert("Please agree to the digital signature");
        return;
      }

      // Validate all sections
      for (let i = 0; i < this.sections.length - 1; i++) {
        this.currentSection = i;
        if (!this.validateCurrentSection()) {
          return;
        }
      }

      this.submitting = true;

      try {
        const payload = this.preparePayload();
        payload.status = 'Self-Assessment Complete';
        payload.selfAssessmentDate = new Date().toISOString();
        payload.employeeSignatureDate = new Date().toISOString().split('T')[0];

        const data = new FormData();
        data.append("function", "submitSelfAppraisal");
        data.append("appraisalData", JSON.stringify(payload));
        if (this.appraisalId) {
          data.append("appraisalId", this.appraisalId);
        }

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Appraisal submitted successfully!") || alert("Appraisal submitted successfully!");
          setTimeout(() => {
            this.$router.push('/my-appraisals');
          }, 2000);
        } else {
          throw new Error(res?.data?.message || "Failed to submit appraisal");
        }
      } catch (error) {
        console.error("Error submitting appraisal:", error);
        this.$toast?.error("Failed to submit appraisal") || alert("Failed to submit appraisal");
      } finally {
        this.submitting = false;
      }
    },

    preparePayload() {
      const mappedTrainings = this.trainingNeeds
        .map((trainingNeed, idx) => ({
          sNo: idx + 1,
          trainingProgram: trainingNeed || '',
          priority: idx + 1
        }))
        .filter(t => (t.trainingProgram || '').trim() !== '');

      const mappedGoals = this.additionalAssignments
        .map((assignment, idx) => ({
          sNo: idx + 1,
          actionPlan: assignment || '',
          targetDate: this.appraisal.periodEnd || ''
        }))
        .filter(g => (g.actionPlan || '').trim() !== '');

      return {
        employeeID: this.employeeInfo.id,
        appraisalYear: this.appraisalYear,
        periodStart: this.appraisal.periodStart,
        periodEnd: this.appraisal.periodEnd,
        positionTitle: this.employeeInfo.position,
        department: this.employeeInfo.department,
        location: this.employeeInfo.location,
        dateOfJoining: this.employeeInfo.dateOfJoining,
        associateComments: this.section4Comments || this.appraisal.associateComments,
        kpas: this.kpas,
        organizationalParams: this.organizationalParams,
        attributes: [],
        jobContent: this.jobContent,
        growthOpportunities: this.growthOpportunities,
        workCulture: this.workCulture,
        colleagues: this.colleagues,
        trainings: mappedTrainings,
        additionalFeedback: {
          thingsIShouldDo: this.midYearReviewNotes,
          managerShouldDo: this.commitmentToTargets ? 'Committed to agreed targets' : '',
          organizationShouldDo: this.additionalFeedback.organizationShouldDo
        },
        goals: mappedGoals
      };
    },

    /* eslint-disable-next-line no-unused-vars */
    getSectionStatus(index) {
      // Logic to determine section status
      return 'Pending';
    },

    /* eslint-disable-next-line no-unused-vars */
    getSectionCompletionBadge(index) {
      return 'hr-badge-warning';
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.self-assessment-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
.hr-mb-0 { margin-bottom: 0; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-mb-4 { margin-bottom: 16px; }
.hr-mt-3 { margin-top: 12px; }
.hr-me-2 { margin-right: 8px; }
.hr-ms-1 { margin-left: 4px; }
.hr-px-3 { padding-left: 12px; padding-right: 12px; }
.hr-py-2 { padding-top: 8px; padding-bottom: 8px; }
.hr-text-end { text-align: right; }
.hr-text-primary { color: #059669; }
.hr-text-muted { color: #64748b; }
.hr-small { font-size: 0.875rem; }
.hr-flex { display: flex; }
.hr-justify-between { justify-content: space-between; }
.hr-align-center { align-items: center; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-progress-wrap { background: #e2e8f0; border-radius: 8px; overflow: hidden; }
.hr-progress-fill { height: 100%; border-radius: 8px; transition: width 0.3s ease; }
.hr-bg-primary { background: #059669; }
.hr-table td, .hr-table th { vertical-align: middle; }
textarea.hr-input { resize: vertical; }
.hr-section-tab { cursor: pointer; }
.hr-border-bottom { border-bottom: 1px solid #e2e8f0; }
.hr-pb-4 { padding-bottom: 16px; }
.hr-p-3 { padding: 12px; }
.hr-bg-light { background: #f8fafc; }
</style>